import { useMemo, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { TUTORIAL_DEMO_NOTE } from '@/config/demoPropertySeed';
import { useRemoveProductMutation } from '@/apis/extended/productApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { ProductBadges } from '@/components/ProductDetail/ProductBadges';
import { useEstimation } from '@/hooks/useEstimation';
import { formatNumber, formatPrice } from '@/utils/helper';
import { Product } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';
import { RemoveProductModal } from '../ConfirmModals/RemoveProductModal';
import { ProductCardImage } from '../ProductCard/ProductCardImage';
import {
  ctaForProperty,
  loanTypeBadgeLabel,
  stageLabelFor,
} from './propertyCardConstants';
import { PropertyStageProgress } from './PropertyStageProgress';

interface PropertyCardProps {
  property: PropertyView;
  product: Product;
  hideDelete?: boolean;
}

/** Copy for Web Share API (`text`); includes county when present on `PropertyView`. */
function propertyShareDescription(property: PropertyView): string {
  const street =
    property.street?.trim() ||
    property.address.split(',')[0]?.trim() ||
    property.address.trim() ||
    'this property';
  const county = property.county?.trim();
  if (county) {
    return `Check out this Unbeatable Property at ${street} in ${county}.`;
  }
  return `Check out this Unbeatable Property at ${street}.`;
}

function primaryPropertyImageUrl(
  property: PropertyView,
  product: Product
): string | undefined {
  const candidates = [
    property.imageUrl,
    product.photo,
    ...(product.photos ?? []),
  ];
  return candidates.find((u) => typeof u === 'string' && u.trim().length > 0);
}

const BADGE_COLOR: Record<'navy' | 'teal' | 'amber', string> = {
  navy: 'var(--primary-container)',
  teal: 'var(--stage-active)',
  amber: 'var(--stage-idle)',
};

export const PropertyCard = ({
  property,
  product,
  hideDelete = false,
}: PropertyCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [removeProduct] = useRemoveProductMutation();
  const [removeOpen, setRemoveOpen] = useState(false);
  const { data: currentUser } = useGetCurrentUserQuery();

  const productForBadges = useMemo(
    () =>
      product.user
        ? product
        : currentUser
        ? { ...product, user: currentUser }
        : product,
    [product, currentUser]
  );

  const { estimation } = useEstimation(
    product,
    product.savedDownPayment ?? property.downPaymentAmt,
    property.loanProgram
  );

  const monthlyPayment =
    property.estimatedMonthlyPayment ??
    (estimation as { piti?: number; principalAndInterest?: number } | null)
      ?.piti ??
    (estimation as { principalAndInterest?: number } | null)
      ?.principalAndInterest;

  const badge = loanTypeBadgeLabel(property);
  const stageLabel = stageLabelFor(property, property.stage);

  const goDealRoom = () => navigate(`/properties/${property.id}`);

  const handleShare = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const url = `${window.location.origin}/properties/${property.id}`;
    const text = propertyShareDescription(property);
    const title =
      property.street?.trim() ||
      property.address.split(',')[0]?.trim() ||
      property.address ||
      'Unbeatable Property';

    const run = async () => {
      if (!navigator.share) {
        await navigator.clipboard.writeText(url);
        toast.success('Property link copied');
        return;
      }

      const imageUrl = primaryPropertyImageUrl(property, product);

      const shareWithOptionalImage = async (): Promise<void> => {
        if (!imageUrl || typeof navigator.canShare !== 'function') {
          await navigator.share({ title, text, url });
          return;
        }
        try {
          const res = await fetch(imageUrl);
          if (!res.ok) {
            await navigator.share({ title, text, url });
            return;
          }
          const blob = await res.blob();
          if (!blob.size || !blob.type.startsWith('image/')) {
            await navigator.share({ title, text, url });
            return;
          }
          const ext =
            blob.type === 'image/png'
              ? 'png'
              : blob.type === 'image/webp'
              ? 'webp'
              : blob.type === 'image/gif'
              ? 'gif'
              : 'jpg';
          const file = new File(
            [blob],
            `unbeatable-property-${property.id}.${ext}`,
            {
              type: blob.type,
            }
          );
          const withFiles: ShareData = { files: [file], title, text, url };
          if (navigator.canShare(withFiles)) {
            await navigator.share(withFiles);
            return;
          }
        } catch {
          /* CORS or unsupported — fall back below */
        }
        await navigator.share({ title, text, url });
      };

      try {
        await shareWithOptionalImage();
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        try {
          await navigator.clipboard.writeText(url);
          toast.success('Property link copied');
        } catch {
          toast.error('Unable to share');
        }
      }
    };

    void run();
  };

  const handleCompare = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    toast('Compare coming soon — saved for later release.');
  };

  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setRemoveOpen(true);
  };

  const onConfirmDelete = async () => {
    try {
      await removeProduct(property.id).unwrap();
      toast.success('Property removed');
    } catch {
      toast.error('Failed to remove property');
    } finally {
      setRemoveOpen(false);
    }
  };

  const idleBorder = useMemo(
    () =>
      property.isIdle
        ? `2px solid ${theme.palette.warning.main}`
        : `1px solid ${theme.palette.divider}`,
    [property.isIdle, theme]
  );

  return (
    <Box
      onClick={goDealRoom}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        minWidth: 0,
        backgroundColor: theme.palette.mainColors.background1,
        border: idleBorder,
        borderRadius: '16px',
        p: 2,
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <ProductCardImage product={product} sx={{ aspectRatio: '16 / 9' }} />
        {badge ? (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: BADGE_COLOR[badge.color],
              color: '#fff',
              px: 1,
              py: 0.5,
              borderRadius: '999px',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.2,
            }}
          >
            {badge.label}
          </Box>
        ) : null}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toast('Saved property');
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(255,255,255,0.9)',
            '&:hover': { backgroundColor: '#fff' },
          }}
        >
          <i className="fa-light fa-bookmark" />
        </IconButton>
      </Box>

      <PropertyStageProgress property={property} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Stage {property.stage}: {stageLabel}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: property.isIdle
              ? theme.palette.warning.main
              : theme.palette.text.secondary,
            fontWeight: property.isIdle ? 700 : 500,
          }}
        >
          {property.daysInCurrentStage}d in this stage
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {formatPrice(property.listPrice, 0)}
        </Typography>
        {monthlyPayment ? (
          <Typography variant="body2" color="text.secondary">
            ~${formatNumber(monthlyPayment, 0)}/mo
          </Typography>
        ) : null}
      </Box>

      <Typography variant="body2" color="text.secondary">
        {property.address || 'Address unavailable'}
      </Typography>

      {product.note === TUTORIAL_DEMO_NOTE ? (
        <Chip
          label="Demo"
          size="small"
          sx={{
            alignSelf: 'flex-start',
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }}
        />
      ) : null}

      {property.isDscr ? (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary }}
        >
          DSCR: {property.dscrRatio ? property.dscrRatio.toFixed(2) : '—'} ·
          Cash Flow:{' '}
          {property.monthlyCashFlow != null
            ? `$${formatNumber(property.monthlyCashFlow, 0)}/mo`
            : '—'}
        </Typography>
      ) : null}

      <Button
        variant="contained"
        fullWidth
        onClick={(e) => {
          e.stopPropagation();
          goDealRoom();
        }}
        sx={{
          height: 40,
          fontWeight: 700,
          textTransform: 'none',
          backgroundColor: '#0a1f44',
          '&:hover': { backgroundColor: '#0f2a5a' },
        }}
      >
        {ctaForProperty(property)}
      </Button>

      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          minHeight: 28,
          mt: 0.5,
        }}
      >
        {productForBadges.user ? (
          <ProductBadges product={productForBadges} />
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Tooltip title="Compare">
            <IconButton size="small" onClick={handleCompare}>
              <i className="fa-light fa-scale-balanced" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share">
            <IconButton size="small" onClick={handleShare}>
              <i className="fa-light fa-share-nodes" />
            </IconButton>
          </Tooltip>
          {!hideDelete && (
            <Tooltip title="Remove">
              <IconButton size="small" onClick={handleDelete}>
                <i className="fa-light fa-trash" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <RemoveProductModal
        isOpen={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onConfirm={onConfirmDelete}
      />
    </Box>
  );
};
